import { Injectable, InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { CompanyViewModel } from '@treasury/domain-components/view-models/company.viewModel';
import { UsersService } from '@treasury/domain/backoffice/services';
import { CompanyUserService } from '@treasury/domain/company/user';
import { CompanyUserSummary } from '@treasury/domain/company/user/entities';
import { Entitlements, EntitlementsService } from '@treasury/domain/entitlements';
import { Feature, FeatureFlagService } from '@treasury/domain/services/feature-flags';
import { TmDialogService } from '@treasury/presentation/services/dialog';
import { TmSideDrawerService } from '@treasury/presentation/services/side-drawer';
import { TmToastService } from '@treasury/presentation/services/toast';
import {
    ResendEnrollmentLinkCommand,
    SecurityAndPasswordSettingsCommand,
    TmAuthSettingsCommand,
    BoViewUserCommand as ViewUserCommand,
} from '../';
import { RowActionCommandResult } from '../../base.cmd';
import { LegacyResetPasswordCommand } from '../../shared/users/commands/legacyResetPassword.cmd';
import {
    CompanyUserActionRecord,
    SharedUsersActionRecord,
    SharedUsersActionType,
} from '../../shared/users/users-actions.types';
import { Decision } from '../approveOrReject/approve-or-reject-drawer/approve-or-reject.component';
import { ApproveOrRejectCompanyUserCommand } from '../approveOrReject/approveOrReject.cmd';
import { CompanyUserActionType, UserActionFlags } from '../user-actions.types';

@Injectable()
export class BoCompanyUserActionHelper {
    constructor(
        private readonly entitlementsService: EntitlementsService,
        dialogService: TmDialogService,
        drawerService: TmSideDrawerService,
        usersService: UsersService,
        companyUserService: CompanyUserService,
        toastService: TmToastService,
        navigationService: NavigationService
    ) {
        this.resendEnrollmentLinkCommand = new ResendEnrollmentLinkCommand(
            dialogService,
            usersService,
            companyUserService,
            toastService
        );
        this.legacyResetPasswordCommand = new LegacyResetPasswordCommand(
            drawerService,
            usersService,
            toastService
        );
        this.viewUserCommand = new ViewUserCommand(navigationService, companyUserService);
        this.securityAndPasswordSettingsCommand = new SecurityAndPasswordSettingsCommand();
        this.authSettingsCommand = new TmAuthSettingsCommand(drawerService, toastService);

        this.approveOrRejectCommand = new ApproveOrRejectCompanyUserCommand();
    }

    @InjectProperty({ async: true, token: FeatureFlagService })
    declare private readonly featureFlagServicePromise: Promise<FeatureFlagService>;

    private readonly resendEnrollmentLinkCommand: ResendEnrollmentLinkCommand;

    private readonly legacyResetPasswordCommand: LegacyResetPasswordCommand<CompanyUserSummary>;

    private readonly viewUserCommand: ViewUserCommand;

    private readonly securityAndPasswordSettingsCommand: SecurityAndPasswordSettingsCommand;

    private readonly authSettingsCommand: TmAuthSettingsCommand;

    private readonly approveOrRejectCommand: ApproveOrRejectCompanyUserCommand;

    private _userActions: RowActionCommandResult<CompanyUserSummary, CompanyUserActionType>[] = [];

    private _flags: UserActionFlags = new UserActionFlags();

    /**
     * Gets the company user actions.
     *
     * @returns An array of company user actions.
     */
    public get userActions() {
        return this._userActions;
    }

    /**
     * Retrieves a list of company user actions with their respective properties.
     *
     * This method generates a list of user actions.
     * Each user action is then mapped to an object containing the following properties:
     * - `id`: The identifier of the user action.
     * - `label`: The label of the user action.
     * - `hiddenCondition`: A boolean indicating if the user action is hidden.
     *
     * @returns {Promise<void>}
     *          A promise that resolves when the user actions have been loaded.
     */
    public async loadCompanyUserActions(company: CompanyViewModel): Promise<void> {
        const featureFlagService = await this.featureFlagServicePromise;
        this._flags = {
            isUisEnabled: await featureFlagService.isEnabled(Feature.UisEnabled),
            canResetUserPassword: await this.entitlementsService.isEnabled(
                Entitlements.ResetUserPassword
            ),
        };

        const actionConfigurations = Object.entries(this.generateCompanyUserActionConfig(company));

        this._userActions = actionConfigurations.map(([type, value]) => {
            return {
                type: type as CompanyUserActionType,
                title: value.title,
                hiddenCondition: value.hiddenCondition || false,
                disabledCondition: value.disabledCondition || false,
                action: value.action,
            };
        });
    }

    private notImplemented() {
        alert('Not Implemented');
    }

    private generateCompanyUserActionConfig(
        company: CompanyViewModel
    ): CompanyUserActionRecord | SharedUsersActionRecord<CompanyUserSummary> {
        const { isUisEnabled, canResetUserPassword } = this._flags;
        return {
            [SharedUsersActionType.LegacyResetPassword]: this.legacyResetPasswordCommand.command(
                isUisEnabled,
                canResetUserPassword
            ),
            [CompanyUserActionType.SecurityAndPasswordSettings]:
                this.securityAndPasswordSettingsCommand.command(isUisEnabled),
            [CompanyUserActionType.AuthenticationSettings]: this.authSettingsCommand.command(
                company,
                isUisEnabled
            ),
            [CompanyUserActionType.Approve]: this.approveOrRejectCommand.command(
                Decision.Approve,
                company.id
            ),
            [CompanyUserActionType.Reject]: this.approveOrRejectCommand.command(
                Decision.Reject,
                company.id
            ),
            [CompanyUserActionType.Unlock]: {
                type: CompanyUserActionType.Unlock,
                title: 'Unlock',
                hiddenCondition: user => !user.isLocked,
                action: this.notImplemented,
            },
            [CompanyUserActionType.Copy]: {
                type: CompanyUserActionType.Copy,
                title: 'Copy User',
                hiddenCondition: user => !user.canCopy,
                action: this.notImplemented,
            },
            [CompanyUserActionType.ResendEnrollmentLink]: this.resendEnrollmentLinkCommand.command(
                company,
                isUisEnabled
            ),
        };
    }
}
